import React, { useContext, useEffect, useState, useRef } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Grid,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';

import CurrencyTextField from '@unicef/material-ui-currency-textfield/dist/CurrencyTextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import DescriptionIcon from '@material-ui/icons/Description'; // Icone para Promissória

import { Formik } from 'formik';
import PrintPreviewModal from '../../Print/PrintPreviewModal';
import { addMonths } from 'date-fns'; // Para cálculo de vencimento

import StoreContext from '../../../Store/context';
import UIDialogTitle from '../../../UI/Dialog/Title/UIDialogTitle';
import useApi from '../../../../utils/useApi';
import {
  emptyObject,
  formattedCurrency,
  maskedCurrency,
} from '../../../../utils/utils';
import UIDialogConfirm from '../../../UI/Dialog/Confirm/UIDialogConfirm';
import '../../cupom.css';
import { preparePromissoryData, printPromissoryNote } from '../../../PromissoryNote/PromissoryNote';

const SaleConfirm = ({ ...children }) => {
  const { credencials, setModalCashierSelect } = useContext(StoreContext);

  const {
    options,
    setOptions,
    calculate,
    handleModal,
    refreshCards,
    handleModalSale,
    mobile,
  } = children;

  const remainder = (options?.total || 0) - (options?.paymentData || []).reduce(
    (value, payment) => {
      const amountStr = String(payment.amountPaid || '0');
      const numericVal = parseFloat(amountStr.replace(/\./g, '').replace(',', '.'));
      return numericVal + value;
    },
    0,
  );

  const [open, setOpen] = useState(false);
  const [modalPrint, setModalPrint] = useState(false);
  const isSubmittingRef = useRef(false);
  const isQuittingRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (children.open) {
      isSubmittingRef.current = false;
      isQuittingRef.current = false;
      setIsSubmitting(false);
    }
  }, [children.open]);

  const [salesLoad, saleInfo, setSaleInfo] = useApi({
    method: 'patch',
    url: '/sales/sessions',
  });

  const [paymentLoad, paymentInfo] = useApi({
    debounceDelay: 500,
    method: 'get',
    url: '/payments/methods',
  });

  const handlePrint = () => {
    setModalPrint(true);
  };

  const promoPaymentMethods = (() => {
    const promo = options.promotion;
    if (!promo || promo.id === 1) return [];
    const raw = promo.paymentMethods;
    if (Array.isArray(raw)) return raw.map(String);
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed.map(String) : [];
      } catch (e) {
        return [];
      }
    }
    return [];
  })();

  const hasPaymentRestriction = promoPaymentMethods.length > 0;
  const filteredPaymentMethods = hasPaymentRestriction
    ? (paymentInfo.data || []).filter((pay) => promoPaymentMethods.includes(String(pay.id)))
    : (paymentInfo.data || []);

  const updateValues = (values, index, toRemove = false) => {
    let newValues = { ...options };

    if (toRemove) newValues.paymentData.splice(index, 1);
    else
      newValues.paymentData[index] = {
        ...values,
        amountPaid: maskedCurrency(values.amountPaid),
      };

    setOptions(newValues);
  };

  const addMorePayment = () => {
    const index = options?.paymentData?.length;
    const defaultMethod = hasPaymentRestriction && filteredPaymentMethods.length > 0
      ? String(filteredPaymentMethods[0].id)
      : '3';
    const values = {
      paymentMethod: defaultMethod,
      amountPaid: remainder > 0 ? maskedCurrency(remainder.toFixed(2)) : 0,
    };
    updateValues(values, index);
  };

  useEffect(() => {
    paymentLoad();
  }, []);

  useEffect(() => {
    if (hasPaymentRestriction && filteredPaymentMethods.length > 0 && options.paymentData && options.paymentData.length > 0) {
      let needsFix = false;
      const fixedPayments = options.paymentData.map((p) => {
        if (!promoPaymentMethods.includes(String(p.paymentMethod))) {
          needsFix = true;
          return { ...p, paymentMethod: String(filteredPaymentMethods[0].id) };
        }
        return p;
      });
      if (needsFix) {
        setOptions({ ...options, paymentData: fixedPayments });
      }
    }
  }, [options.promotion, paymentInfo.data]);

  function onQuit() {
    if (isQuittingRef.current) return;
    isQuittingRef.current = true;
    isSubmittingRef.current = false;
    setIsSubmitting(false);
    setSaleInfo();
    handleModal();
    if (handleModalSale) handleModalSale();
    if (saleInfo.data) {
      children.resetform();
    }
    setTimeout(() => {
      isQuittingRef.current = false;
    }, 400);
  }

  async function onSale() {
    if (isSubmittingRef.current || saleInfo.loading || isSubmitting || saleInfo.data) {
      return;
    }
    isSubmittingRef.current = true;
    setIsSubmitting(true);

    try {
      if (!credencials.cashSession) {
        setModalCashierSelect(true);
        isSubmittingRef.current = false;
        setIsSubmitting(false);
        return;
      }

      const paymentData = options.paymentData.map((item) => ({
        ...item,
        amountPaid: item.amountPaid.replace('.', '')?.replace(',', '.'),
      }));

      if (hasPaymentRestriction) {
        const invalidPay = paymentData.find((p) => !promoPaymentMethods.includes(String(p.paymentMethod)));
        if (invalidPay) {
          alert(`A promoção '${options.promotion?.name}' só permite pagamentos via: ${filteredPaymentMethods.map((p) => p.name).join(', ')}`);
          isSubmittingRef.current = false;
          setIsSubmitting(false);
          return;
        }
      }

      const saleFinal = {
        ...calculate,
        onlyCalculate: 0,
        cashRegister: credencials.cashSession,
        customer: options.customer?.id || calculate.customer,
        session_id: calculate.session_id,
        splitRemaining: calculate.splitRemaining,
        paymentData: paymentData,
        promotion: options.promotion?.id || 1,
        observation: `${calculate.identifier} - ${calculate.responsible}`,
      };

      await salesLoad({
        data: saleFinal,
        onCompleted: (res) => {
          isSubmittingRef.current = false;
          setIsSubmitting(false);
          if (res && res.data) {
            refreshCards({});
          }
        },
        onError: () => {
          isSubmittingRef.current = false;
          setIsSubmitting(false);
        },
      });
      setOpen(true);
    } catch (err) {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  // --- Função para Gerar Promissória ---
  const handlePrintPromissory = () => {
    if (!saleInfo.data) return;

    // Prioriza o customerData retornado da venda, fallback para options.customer
    const clientData = saleInfo.data.customerData || options.customer;

    if (!clientData) return;

    const saleDate = new Date();
    let installmentsList = null;

    // Verifica se há parcelas geradas
    if (saleInfo.data.plots && Object.keys(saleInfo.data.plots).length > 0) {
      installmentsList = Object.entries(saleInfo.data.plots)
        .map(([key, value]) => {
          const rawValue = String(value);
          if (rawValue.toLowerCase().includes('paga')) return null;

          const amount = parseFloat(rawValue.split(' -')[0]);
          const dueDate = addMonths(saleDate, Number(key));

          return {
            number: key,
            value: amount,
            dueDate: dueDate
          };
        })
        .filter(Boolean);
    }

    // Fallback para saldo devedor sem parcelas explícitas
    if (!installmentsList || installmentsList.length === 0) {
      const debt = parseFloat(saleInfo.data.value) - parseFloat(saleInfo.data.amountPaid);
      if (debt > 0.01) {
        const noteData = preparePromissoryData({
          client: clientData,
          companyData: credencials.companyData,
          totalValue: debt,
          dueDate: addMonths(saleDate, 1),
          emissionDate: saleDate,
        });
        printPromissoryNote(noteData);
        return;
      }
      return;
    }

    const noteData = preparePromissoryData({
      client: clientData,
      companyData: credencials.companyData,
      totalValue: saleInfo.data.value,
      emissionDate: saleDate,
      installments: installmentsList
    });

    printPromissoryNote(noteData);
  };

  const showPromissoryButton = saleInfo.data &&
    (saleInfo.data.customerData || options.customer) &&
    saleInfo.data.customer != 1 &&
    (parseFloat(saleInfo.data.amountPaid) < parseFloat(saleInfo.data.value));

  return (
    <>
      <Dialog
        fullWidth
        open={children.open}
        onClose={onQuit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            if (saleInfo.data) onQuit();
            else if (!isSubmittingRef.current && !saleInfo.loading && !isSubmitting) onSale();
          }
        }}
      >
        <UIDialogTitle onClose={onQuit}>
          {saleInfo.data ? 'Venda concluida' : 'Concluir venda'}
        </UIDialogTitle>
        <DialogContent>
          {!saleInfo.data && (
            <Grid container direction="row" spacing={0}>
              <Grid item xs={12}>
                {hasPaymentRestriction && (
                  <Box mb={2} p={1.5} bgcolor="#E8F5E9" borderRadius={6} border="1px solid #A5D6A7">
                    <Typography variant="body2" style={{ color: '#2E7D32', fontWeight: 600 }}>
                      Promoção restrita às formas: {filteredPaymentMethods.map((p) => p.name).join(', ') || 'Formas permitidas'}
                    </Typography>
                  </Box>
                )}
                {!options.paymentData.length && (
                  <Typography align="center" variant="h6" component="div">
                    <Button
                      onClick={addMorePayment}
                      color="primary"
                      disabled={saleInfo.loading}
                    >
                      Informar pagamento a vista
                    </Button>
                  </Typography>
                )}
                {options.paymentData &&
                  options.paymentData.map((payment, index) => (
                    <React.Fragment key={index}>
                      <Formik initialValues={payment}>
                        {({ setFieldValue, values }) => (
                          <Grid
                            container
                            direction="row"
                            spacing={2}
                            justifyContent="center"
                            alignItems="center"
                          >
                            <Grid item xs md>
                              <TextField
                                value={values.amountPaid}
                                name="amountPaid"
                                type="tel"
                                label="Valor pago"
                                variant="outlined"
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment
                                      position="start"
                                      variant="outlined"
                                      sx={{ fontSize: 1 }}
                                    >
                                      <Select
                                        name="paymentMethod"
                                        value={values.paymentMethod}
                                        onChange={(ev) => {
                                          setFieldValue(
                                            'paymentMethod',
                                            ev.target.value,
                                          );
                                        }}
                                        onBlur={() =>
                                          updateValues(values, index)
                                        }
                                        labelId="sale-confirm__paymentMethod"
                                        renderValue={
                                          mobile
                                            ? () => (
                                              <Tooltip title="Método de pagamento">
                                                <Typography
                                                  align="center"
                                                  variant="h6"
                                                  component="div"
                                                >
                                                  {'>'}
                                                </Typography>
                                              </Tooltip>
                                            )
                                            : null
                                        }
                                      >
                                        {filteredPaymentMethods &&
                                          filteredPaymentMethods.map((pay) => (
                                            <MenuItem
                                              key={pay.id}
                                              value={pay.id}
                                            >
                                              {pay.name}
                                            </MenuItem>
                                          ))}
                                      </Select>
                                    </InputAdornment>
                                  ),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <Button
                                        onClick={addMorePayment}
                                        color="primary"
                                        disabled={saleInfo.loading}
                                      >
                                        +
                                      </Button>
                                      <Button
                                        onClick={() =>
                                          updateValues({}, index, true)
                                        }
                                        color="secondary"
                                        disabled={saleInfo.loading}
                                      >
                                        -
                                      </Button>
                                    </InputAdornment>
                                  ),
                                }}
                                onChange={(ev) =>
                                  setFieldValue(
                                    'amountPaid',
                                    maskedCurrency(ev.target.value),
                                  )
                                }
                                onBlur={() => updateValues(values, index)}
                                fullWidth
                              />
                            </Grid>
                          </Grid>
                        )}
                      </Formik>
                    </React.Fragment>
                  ))}
                {options.paymentData.length > 0 && (
                  <Typography align="center" variant="h6" component="div">
                    {remainder >= 0 ? 'Faltando' : 'Troco'}:{' '}
                    {formattedCurrency(Math.abs(remainder))}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} md={12}>
                <TextField
                  value={options.observation}
                  onChange={(ev) =>
                    setOptions({ ...options, observation: ev.target.value })
                  }
                  label="Observações"
                  placeholder="Anote alguma observação da venda"
                  fullWidth
                />
              </Grid>
            </Grid>
          )}
          {saleInfo.data && (
            <Grid
              container
              direction="row"
              spacing={1}
              className="sale-resume-print"
            >
              <TableContainer>
                <Typography align="center" variant="h6" component="div">
                  Resumo do pedido
                </Typography>
                <Typography align="center" variant="h6" component="div">
                  #{saleInfo.data.saleId}
                </Typography>
                <Table size="small">
                  <TableBody>
                    <TableRow>
                      <TableCell>Cliente</TableCell>
                      <TableCell align="right">
                        {saleInfo.data.customer == 1
                          ? 'Avulso'
                          : `${saleInfo.data.customerData?.name || 'Avulso'} ${saleInfo.data.customerData?.lastName || ''
                          }`}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Vendedor</TableCell>
                      <TableCell align="right">{credencials.name}</TableCell>
                    </TableRow>
                    {options.promotion && (
                      <TableRow>
                        <TableCell>Promoção aplicada</TableCell>
                        <TableCell align="right">{options.promotion.name}</TableCell>
                      </TableRow>
                    )}
                    {saleInfo.data.details.discount > 0 && (
                      <TableRow>
                        <TableCell>Desconto</TableCell>
                        <TableCell align="right">
                          {formattedCurrency(saleInfo.data.details.discount)}
                        </TableCell>
                      </TableRow>
                    )}
                    {saleInfo.data.details.increase > 0 && (
                      <TableRow>
                        <TableCell>Acréscimo juros</TableCell>
                        <TableCell align="right">
                          {formattedCurrency(saleInfo.data.details.increase)}
                        </TableCell>
                      </TableRow>
                    )}
                    <TableRow>
                      <TableCell>Valor da compra</TableCell>
                      <TableCell align="right">
                        {formattedCurrency(saleInfo.data.value)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Valor pago</TableCell>
                      <TableCell align="right">
                        {formattedCurrency(saleInfo.data.amountPaid)}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Parcelas</TableCell>
                      <TableCell align="right">
                        {Object.entries(saleInfo.data.plots).map(
                          ([key, value]) => (
                            <Typography key={key} variant="body2" style={{ padding: 0, margin: 0 }}>
                              {key}ª
                              {typeof value === 'string'
                                ? ` de ${formattedCurrency(
                                  Number(value.split(' -')[0]),
                                )} - Paga`
                                : ` de ${formattedCurrency(value)}`}
                            </Typography>
                          ),
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Data da compra</TableCell>
                      <TableCell align="right">{new Date().toLocaleString()}</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={2} style={{ fontWeight: 'bold', paddingTop: '10px' }}>
                        Itens
                      </TableCell>
                    </TableRow>
                    {saleInfo.data.details.products.map((p, index) => {
                      const itemSubtotal = p.subtotal !== undefined ? Number(p.subtotal) : (Number(p.price) * Number(p.amount));
                      const itemDiscount = Number(p.discount) || 0;
                      const itemTotal = p.total !== undefined ? Number(p.total) : (itemSubtotal - itemDiscount);

                      return (
                        <TableRow key={`prod-${index}`}>
                          <TableCell>
                            <div>{p.amount}x {p.item}</div>
                            {itemDiscount > 0 && (
                              <Typography variant="caption" style={{ color: '#16A34A', fontWeight: 600 }}>
                                Desconto: -{formattedCurrency(itemDiscount)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {itemDiscount > 0 ? (
                              <div>
                                <span style={{ textDecoration: 'line-through', color: '#94A3B8', fontSize: '0.8rem', marginRight: 4 }}>
                                  {formattedCurrency(itemSubtotal)}
                                </span>
                                <span style={{ fontWeight: 600 }}>{formattedCurrency(itemTotal)}</span>
                              </div>
                            ) : (
                              formattedCurrency(itemSubtotal)
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                    {saleInfo.data.details.services.map((p, index) => {
                      const itemSubtotal = p.subtotal !== undefined ? Number(p.subtotal) : (Number(p.price) * Number(p.amount));
                      const itemDiscount = Number(p.discount) || 0;
                      const itemTotal = p.total !== undefined ? Number(p.total) : (itemSubtotal - itemDiscount);

                      return (
                        <TableRow key={`serv-${index}`}>
                          <TableCell>
                            <div>{p.amount}x {p.item}</div>
                            {itemDiscount > 0 && (
                              <Typography variant="caption" style={{ color: '#16A34A', fontWeight: 600 }}>
                                Desconto: -{formattedCurrency(itemDiscount)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            {itemDiscount > 0 ? (
                              <div>
                                <span style={{ textDecoration: 'line-through', color: '#94A3B8', fontSize: '0.8rem', marginRight: 4 }}>
                                  {formattedCurrency(itemSubtotal)}
                                </span>
                                <span style={{ fontWeight: 600 }}>{formattedCurrency(itemTotal)}</span>
                              </div>
                            ) : (
                              formattedCurrency(itemSubtotal)
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
              {saleInfo.data.payment !== null && (
                <Typography
                  variant="h6"
                  component="div"
                  color="textSecondary"
                  style={{ marginTop: '10px', paddingLeft: '10px' }}
                >
                  TROCO: {formattedCurrency(saleInfo.data.remnant)}
                </Typography>
              )}
            </Grid>
          )}
        </DialogContent>

        <DialogActions>
          {showPromissoryButton && (
            <Button
              onClick={handlePrintPromissory}
              color="primary"
              startIcon={<DescriptionIcon />}
              disabled={saleInfo.loading}
            >
              Promissória
            </Button>
          )}
          {saleInfo.data && (
            <Button
              onClick={handlePrint}
              color="primary"
              autoFocus
              disabled={saleInfo.loading}
            >
              Imprimir Cupom
            </Button>
          )}
          <Button
            onClick={saleInfo.data ? onQuit : onSale}
            color={saleInfo.data ? 'secondary' : 'primary'}
            autoFocus
            disabled={saleInfo.loading || (!saleInfo.data && isSubmitting)}
            style={((!saleInfo.data && isSubmitting) || saleInfo.loading) ? { pointerEvents: 'none' } : {}}
          >
            {saleInfo.data ? (
              'Sair'
            ) : isSubmitting || saleInfo.loading ? (
              <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <CircularProgress size={18} color="inherit" style={{ marginRight: 8 }} />
                Finalizando...
              </span>
            ) : (
              'Confirmar'
            )}
          </Button>
        </DialogActions>
      </Dialog>
      <UIDialogConfirm
        open={open}
        data={saleInfo}
        messageSucess="Venda realizada com sucesso!"
        close={() => setOpen(!open)}
        setrequestinfo={undefined}
        type="success"
      />
      <PrintPreviewModal
        open={modalPrint}
        handleClose={() => setModalPrint(false)}
        saleData={saleInfo.data}
        credencials={credencials}
        options={options}
      />
    </>
  );
};

export default SaleConfirm;